import {useRef, useState} from 'react';
import {Link} from 'react-router';

import {distributorT} from '@/lib/types/distributor.types';
import {statsRowT} from '@/lib/types/stats.types';

import {renderDistributorTableStats} from '@/lib/config/distributorTableConfig';
import {createDataTableConfig} from '@/lib/helpers/config';

import {useMarket} from '@/pages/(shared)/market/providers/marketDetailsProvider';

import {useViewQuery} from '@/hooks';

import {useAuth} from '@/providers/components/authProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

import {ConfirmDeleteModalComponent, SearchAndSelectComponent, StatsRowComponent} from '@/components';
import {DataTable, DataTableRefT} from '@/components/dataTable';

/**
 * Market Detail Distributors Component
 */
function MarketDetailDistributors() {
  const {toast} = useNotifications();
  const {isAdmin} = useAuth();
  const {marketId, searchDistributorsToAdd, addDistributorToMarket, removeDistributorFromMarket} = useMarket();

  // State
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [searchModalVisible, setSearchModalVisible] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<distributorT[] | []>([]);

  // Refs
  const dataTableRef = useRef<DataTableRefT>(null);
  const dataRowToDeleteRef = useRef<distributorT | null>(null);

  // TranStack Queries
  const {data: stats} = useViewQuery({
    view: 'distributor_stats',
  });

  // Data Table Config
  const dataTableConfig = createDataTableConfig({
    enabled: !!marketId,
    table: 'Market_Distributor',
    title: 'Distributors Summary',
    columns: [
      'fk_market_id',
      'date_removed',
      ['Distributor', ['distributor_id', 'distributor_name', 'distributor_city']],
    ],
    searchColumn: 'Distributor.distributor_name',
    filters: [{column: 'fk_market_id', operator: 'eq', value: marketId}],
    addButtonLabel: 'Add Distributor To Market',
    noResultsMessage: 'No distributors in market',
    preventScrolling: true,
    onAdd: () => setSearchModalVisible(true),
    columnsDisplay: [
      {header: 'ID', field: 'Distributor.distributor_id'},
      {
        header: 'Status',
        field: 'date_created',
        render: distributor => (
          <span className={`badge bg-${!distributor.date_removed ? 'success' : 'danger'} text-white`}>
            {!distributor.date_removed ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        header: 'Name',
        field: 'Distributor.distributor_name',
        render: row => (
          <Link
            className="text-primary hover:text-primary dark:text-primary"
            to={`/distributors/${row.Distributor.distributor_id}`}
          >
            {row.Distributor.distributor_name}
          </Link>
        ),
      },
      {
        header: 'Location',
        field: 'Distributor.distributor_city',
        render: row => <p>{`${row.Distributor.distributor_city}, ${row.Distributor.distributor_state}`}</p>,
      },
    ],
    actionsDisplay: [
      {
        icon: 'ri-delete-bin-line',
        color: 'danger',
        handler: async row => {
          dataRowToDeleteRef.current = row.Distributor;
          setDeleteModalVisible(true);
        },
        permission: () => isAdmin,
      },
    ],
  });

  // Stats
  const statsRow: statsRowT = renderDistributorTableStats(stats?.total, stats?.active, stats?.inactive);

  // Functions
  // Function used to close the search modal
  const handleCloseSearchModal = () => {
    setSearchModalVisible(false);
    setSearchResults([]);
  };

  // Function used to get the search results
  const onSearch = async (query: string) => {
    const results = await searchDistributorsToAdd(query);
    setSearchResults(results);
  };

  // Function used to add a row to the market
  const handleAddToMarket = async (distributor: distributorT) => {
    try {
      await addDistributorToMarket(distributor.distributor_id);
      dataTableRef.current?.refetch();
    } catch (error) {
      toast.error({
        title: 'Error Adding Distributor To Market',
        message: (error as Error).message,
      });
    } finally {
      setSearchResults([]);
      setSearchModalVisible(false);
    }
  };

  // Function used to delete a row from the market
  const onDeleteConfirm = async () => {
    if (!dataRowToDeleteRef.current) throw new Error('Distributor not found');

    try {
      await removeDistributorFromMarket(dataRowToDeleteRef.current.distributor_id);
      toast.success({
        title: 'Distributor Removed from market',
        message: `Distributor ${dataRowToDeleteRef.current.distributor_name} removed from market`,
      });
      dataTableRef.current?.refetch();
    } catch (error) {
      toast.error({
        title: 'Error Removing Distributor From Market',
        message: (error as Error).message,
      });
    } finally {
      setDeleteModalVisible(false);
      dataTableRef.current?.refetch();
    }
  };

  return (
    <>
      <StatsRowComponent stats={statsRow} />
      <DataTable ref={dataTableRef} {...dataTableConfig} />
      <SearchAndSelectComponent
        title="Search and Select Distributor"
        isVisible={searchModalVisible}
        onClose={handleCloseSearchModal}
        onSelect={handleAddToMarket}
        onSearch={onSearch}
        results={searchResults}
        mapStructure={{
          displayFields: [{title: 'Distributor Name', field: 'distributor_name'}],
          valueField: 'distributor_id',
        }}
      />
      <ConfirmDeleteModalComponent
        isVisible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        title="Confirm Deletion"
        description="Are you sure you want to remove this distributor from the market?"
        onConfirm={onDeleteConfirm}
      />
    </>
  );
}

export default MarketDetailDistributors;
